/**
 * GlobeCanvas - Main Three.js canvas for the globe visualization.
 *
 * Wraps react-three-fiber Canvas with globe-specific setup and
 * integrates with the GlobeMapProvider context.
 */

import { Suspense, useCallback, useMemo, type ReactNode } from 'react'
import { Canvas } from '@react-three/fiber'
import { GlobeControls } from './GlobeControls'
import { SimpleMorphingHemisphere } from './TiledMorphingHemisphere'
import { CountryGlobe } from './CountryGlobe'
import { CountryLabels } from './CountryLabels'
import { CityLabels } from './CityLabels'
import { useGlobeMap } from '@/domain/globe/hooks/useGlobeMap'
import { useGlobeRenderer } from '@/domain/globe/hooks/useGlobeRenderer'
import { useGeoFeatureData } from '@/domain/globe/hooks/useGeoFeatureData'
import { GLOBE_CONFIG } from '@/domain/globe/constants'
import { filterLakesByZoom } from '@/domain/globe/constants/lakeVisibility'
import { cn } from '@/lib/style'

interface GlobeCanvasProps {
  /** Additional CSS classes */
  className?: string
  /** Children to render inside the canvas */
  children?: ReactNode
  /** Callback when user clicks on the globe surface */
  onLocationClick?: (lat: number, lng: number) => void
}

/**
 * Inner component that uses the globe context.
 * Must be rendered inside GlobeMapProvider.
 */
function GlobeScene({
  onLocationClick,
  children,
}: Omit<GlobeCanvasProps, 'className'>) {
  const { center, zoom, heading, panTo, setZoom, setHeading, cancelAnimation, pauseAutoPan, curvature } = useGlobeMap()
  const { fov } = useGlobeRenderer()

  // Load geographic feature data (countries, lakes, provinces, cities)
  const { countries, lakes, provinces, cities } = useGeoFeatureData()

  // Handle rotation from controls
  const handleRotate = useCallback(
    (deltaLat: number, deltaLng: number) => {
      const newLat = Math.max(
        GLOBE_CONFIG.MIN_LATITUDE,
        Math.min(GLOBE_CONFIG.MAX_LATITUDE, center.latitude + deltaLat)
      )
      const newLng = center.longitude + deltaLng
      panTo(newLat, newLng)
    },
    [center.latitude, center.longitude, panTo]
  )

  // Handle zoom from controls
  const handleZoom = useCallback(
    (delta: number) => {
      setZoom(zoom + delta)
    },
    [zoom, setZoom]
  )

  // Filter lakes based on zoom level (hide smaller lakes when zoomed out)
  const visibleLakes = useMemo(() => filterLakesByZoom(lakes, zoom), [lakes, zoom])

  return (
    <>
      {/* Ambient light for uniform illumination */}
      <ambientLight intensity={1} />

      {/* Camera controls */}
      <GlobeControls
        targetLat={center.latitude}
        targetLng={center.longitude}
        heading={heading}
        fov={fov}
        curvature={curvature}
        onRotate={handleRotate}
        onZoom={handleZoom}
        onLocationClick={onLocationClick}
        onInteractionStart={() => {
          cancelAnimation()
          pauseAutoPan()
        }}
      />

      {/* Globe geometry - POC using CountryGlobe */}
      <Suspense fallback={<SimpleMorphingHemisphere curvature={curvature} />}>
        <CountryGlobe
          countries={countries}
          lakes={visibleLakes}
          provinces={provinces}
          zoom={zoom}
          curvature={curvature}
        />
        <CountryLabels countries={countries} centerLat={center.latitude} centerLng={center.longitude} zoom={zoom} />
        <CityLabels cities={cities} centerLat={center.latitude} centerLng={center.longitude} zoom={zoom} />
      </Suspense>

      {/* Additional children (markers, etc.) */}
      {children}
    </>
  )
}

/**
 * GlobeCanvas component - the main entry point for rendering the globe.
 *
 * This component must be used within a GlobeMapProvider context.
 * It sets up the Three.js canvas with proper camera configuration
 * and integrates all globe components.
 *
 * @example
 * ```tsx
 * <GlobeMapProvider>
 *   <GlobeCanvas
 *     onLocationClick={(lat, lng) => console.log('Clicked:', lat, lng)}
 *   >
 *     <TripMarkers />
 *   </GlobeCanvas>
 * </GlobeMapProvider>
 * ```
 */
export function GlobeCanvas({
  className,
  children,
  onLocationClick,
}: GlobeCanvasProps) {
  return (
    <div className={cn('w-full h-full', className)}>
      <Canvas
        camera={{
          position: [0, 0, 0],
          fov: GLOBE_CONFIG.DEFAULT_FOV,
          near: GLOBE_CONFIG.NEAR_CLIP,
          far: GLOBE_CONFIG.FAR_CLIP,
        }}
        gl={{
          antialias: true,
          alpha: false,
          powerPreference: 'high-performance',
        }}
        style={{ background: '#0a0a14' }}
      >
        <GlobeScene onLocationClick={onLocationClick}>
          {children}
        </GlobeScene>
      </Canvas>
    </div>
  )
}

/**
 * Standalone GlobeCanvas that doesn't require GlobeMapProvider.
 * Useful for simple demos or testing.
 */
export function StandaloneGlobeCanvas({
  className,
  curvature = 1,
  wireframe = false,
}: {
  className?: string
  curvature?: number
  wireframe?: boolean
}) {
  return (
    <div className={cn('w-full h-full', className)}>
      <Canvas
        camera={{
          position: [0, 0, 0],
          fov: GLOBE_CONFIG.DEFAULT_FOV,
          near: GLOBE_CONFIG.NEAR_CLIP,
          far: GLOBE_CONFIG.FAR_CLIP,
        }}
        gl={{
          antialias: true,
          alpha: false,
        }}
        style={{ background: '#0a0a14' }}
      >
        <ambientLight intensity={1} />
        <SimpleMorphingHemisphere curvature={curvature} wireframe={wireframe} />
      </Canvas>
    </div>
  )
}
